import { ReservationResponse } from '@/repositories/ReservationRepository'
import {
  Badge,
  Box,
  IconButton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { FiEye } from 'react-icons/fi'
import { statusOptions } from '../../../../hooks/useReservations'
import { Pagination } from '../../../ui/Pagination'

interface ReservationListProps {
  reservations: ReservationResponse[]
  onViewDetails: (reservation: ReservationResponse) => void
  isLoading: boolean
  page: number
  limit: number
  totalCount: number
  onPageChange: (page: number) => void
}

export function ReservationList({
  reservations,
  onViewDetails,
  isLoading,
  page,
  limit,
  totalCount,
  onPageChange,
}: ReservationListProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00')
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  if (isLoading) {
    return (
      <VStack justify="center" h="200px">
        <Spinner />
        <Text>Carregando reservas...</Text>
      </VStack>
    )
  }

  return (
    <Box overflowX="auto">
      <Table
        variant="simple"
        bg="white"
        borderRadius="lg"
        boxShadow="base"
        overflow="hidden"
      >
        <Thead bg="gray.50">
          <Tr>
            <Th>Propriedade</Th>
            <Th>Cliente</Th>
            <Th>Check-in</Th>
            <Th>Check-out</Th>
            <Th isNumeric>Valor Total</Th>
            <Th>Status</Th>
            <Th width="50px"></Th>
          </Tr>
        </Thead>
        <Tbody>
          {reservations.map((reservation) => (
            <Tr key={reservation.id} _hover={{ bg: 'gray.50' }}>
              <Td fontSize="sm">{reservation.properties?.title}</Td>
              <Td fontSize="sm">{reservation.client?.name}</Td>
              <Td fontSize="sm">{formatDate(reservation.check_in_date)}</Td>
              <Td fontSize="sm">{formatDate(reservation.check_out_date)}</Td>
              <Td isNumeric fontSize="sm">
                {formatCurrency(reservation.reserve_total)}
              </Td>
              <Td>
                <Badge
                  colorScheme={
                    statusOptions[reservation.status]?.color || 'gray'
                  }
                >
                  {statusOptions[reservation.status]?.label ||
                    reservation.status}
                </Badge>
              </Td>
              <Td>
                <IconButton
                  icon={<FiEye />}
                  variant="ghost"
                  size="sm"
                  aria-label="Ver detalhes da reserva"
                  onClick={() => onViewDetails(reservation)}
                />
              </Td>
            </Tr>
          ))}
          {reservations.length === 0 && !isLoading && (
            <Tr>
              <Td colSpan={8} textAlign="center" py={8}>
                <Text color="gray.500" fontSize="md">
                  Nenhuma reserva encontrada
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <Pagination
        page={page}
        limit={limit}
        totalCount={totalCount}
        onPageChange={onPageChange}
      />
    </Box>
  )
}
